/**
 * WCAG contrast math and the ink/background helpers that keep a logo legible
 * over a palette-derived gradient. Pure and dependency-free so the browser and
 * the image renderer share one implementation.
 */
/** WCAG 2.1 SC 1.4.11 minimum for graphical objects. */
export const CONTRAST_TARGET_GRAPHICAL = 3;
const DEFAULT_ALPHA_THRESHOLD = 200;
const DEFAULT_QUANTIZE_BITS = 4;
const DEFAULT_TRIM_PERCENTILE = 0.02;
const MAX_HALO_OPACITY = 0.55;
/** Strength when only a minority of the mark fails; the bulk already reads. */
const MINORITY_HALO_OPACITY = 0.3;
const MIN_HALO_BLUR_PX = 2;
const MAX_HALO_BLUR_PX = 6;
const BISECTION_STEPS = 24;
/** Interpolated points a gradient is verified at, beyond its declared stops. */
const MIDPOINT_SAMPLES = [0.25, 0.5, 0.75];
/**
 * Relative luminance per WCAG 2.x.
 *
 * @param color Hex (`#rgb`/`#rrggbb`) or `rgb()` color.
 * @returns Luminance in [0, 1], or `null` when unparseable.
 */
export function relativeLuminance(color) {
    const rgb = parseColor(color);
    return rgb ? luminanceOf(rgb) : null;
}
/**
 * Contrast ratio between two colors.
 *
 * @returns Ratio in [1, 21], or `null` when either color is unparseable.
 */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    if (la === null || lb === null)
        return null;
    return ratioBetween(la, lb);
}
/**
 * Interpolates two colors in sRGB, matching how browsers paint a gradient.
 *
 * @param amount Position in [0, 1] from `a` to `b`.
 */
export function blendColors(a, b, amount) {
    const from = parseColor(a);
    const to = parseColor(b);
    if (!from || !to)
        return null;
    const t = clamp(amount, 0, 1);
    return toHex({
        r: from.r + (to.r - from.r) * t,
        g: from.g + (to.g - from.g) * t,
        b: from.b + (to.b - from.b) * t,
    });
}
/**
 * Corrects a gradient against a logo's ink by compressing every stop toward
 * one end by the same proportion, which clears the ramp without collapsing the
 * stops into each other at the extreme.
 *
 * @returns The corrected stops and the worst ratio across the ramp.
 */
export function contrastSafeStops(stops, ink, options = {}) {
    const minRatio = options.minRatio ?? CONTRAST_TARGET_GRAPHICAL;
    const gray = clamp(options.grayscale ?? 0, 0, 1);
    const inkColor = parseColor(ink?.dominant ?? "");
    const parsed = stops.map((stop) => parseColor(stop));
    if (!stops.length || !inkColor || parsed.some((rgb) => !rgb))
        return { stops, ratio: 1 };
    const inkLuminance = effectiveLuminance(inkColor, gray);
    // The side is settled at the floor, so cards never end up split between pale
    // and dark. A target above the floor is then a preference, kept only when
    // that side can reach it.
    const direction = preferredDirection(clearanceBounds(inkLuminance, CONTRAST_TARGET_GRAPHICAL));
    const bounds = clearanceBounds(inkLuminance, minRatio);
    const reachable = direction === "darken" ? bounds.dark >= 0 : bounds.light <= 1;
    const target = reachable ? minRatio : CONTRAST_TARGET_GRAPHICAL;
    // Compressing toward the extreme rather than subtracting a constant keeps
    // every stop inside the range, so none of them clamp together into one color.
    const hsls = parsed.map((rgb) => rgbToHsl(rgb));
    const shifted = (amount) => hsls.map((hsl) => quantize(hslToRgb({
        ...hsl,
        l: direction === "darken"
            ? hsl.l * (1 - amount)
            : 1 - (1 - hsl.l) * (1 - amount),
    })));
    // Every stop moves monotonically toward the extreme, and that extreme clears
    // by construction, so the worst ratio rises with the shift and bisects.
    const asPainted = worstRatio(parsed, inkLuminance, gray);
    if (asPainted >= target) {
        return { stops, ratio: asPainted };
    }
    let fail = 0;
    let pass = 1;
    for (let i = 0; i < BISECTION_STEPS; i += 1) {
        const mid = (fail + pass) / 2;
        if (worstRatio(shifted(mid), inkLuminance, gray) >= target)
            pass = mid;
        else
            fail = mid;
    }
    const corrected = shifted(pass);
    return {
        stops: corrected.map((rgb) => toHex(rgb)),
        ratio: worstRatio(corrected, inkLuminance, gray),
    };
}
/**
 * Black or white, whichever reads better against the given background.
 *
 * @param options `grayscale` is the filter amount the surface renders under.
 */
export function pickScrimColor(background, options = {}) {
    const rgb = parseColor(background);
    if (!rgb)
        return "#000000";
    return scrimForLuminance(effectiveLuminance(rgb, clamp(options.grayscale ?? 0, 0, 1)));
}
/**
 * The halo a logo needs over a painted gradient, growing as more of the mark
 * loses separation from the field.
 *
 * @param stops Gradient stops as painted, already corrected.
 * @returns `opacity: 0` when no halo is warranted.
 */
export function logoHaloSpec(stops, ink, options = {}) {
    // A target of 1 or less asks for no separation at all, and would divide by
    // zero below.
    const minRatio = Math.max(options.minRatio ?? CONTRAST_TARGET_GRAPHICAL, 1.0001);
    const gray = clamp(options.grayscale ?? 0, 0, 1);
    const none = { color: "#000000", opacity: 0, blurPx: 0 };
    if (!ink || !stops.length)
        return none;
    const worst = worstSampleForMark(stops, ink, gray);
    if (worst.ratio >= minRatio)
        return none;
    // Polarity comes from the sample that reads worst, judged through the same
    // filter the surface renders under. An unreadable field leaves only the ink
    // to separate the halo from, so it decides instead.
    const color = worst.sample
        ? pickScrimColor(worst.sample, { grayscale: gray })
        : pickScrimColor(ink.dominant, { grayscale: gray });
    // A mark whose bulk reads owes its failure to a minority of its ink, which a
    // light ring separates; no flat field can carry both ends of a wide span.
    const bulk = worstRatioAcross(stops, ink.dominant, gray);
    const shortfall = (minRatio - clamp(bulk, 1, minRatio)) / (minRatio - 1);
    const opacity = round(bulk >= minRatio
        ? MINORITY_HALO_OPACITY
        : MINORITY_HALO_OPACITY + (MAX_HALO_OPACITY - MINORITY_HALO_OPACITY) * shortfall, 4);
    const blurPx = Math.round(MIN_HALO_BLUR_PX + (MAX_HALO_BLUR_PX - MIN_HALO_BLUR_PX) * (opacity / MAX_HALO_OPACITY));
    return { color, opacity, blurPx };
}
/**
 * Reduces RGBA bytes to a mark's dominant color and its trimmed extremes.
 *
 * @param pixels Tightly packed RGBA bytes.
 * @returns The profile, or `null` when no pixel clears the alpha threshold.
 */
export function inkProfileFromPixels(pixels, options = {}) {
    const alphaThreshold = options.alphaThreshold ?? DEFAULT_ALPHA_THRESHOLD;
    const bits = options.quantizeBits ?? DEFAULT_QUANTIZE_BITS;
    const trim = options.trimPercentile ?? DEFAULT_TRIM_PERCENTILE;
    const step = 256 / (1 << bits);
    const buckets = new Map();
    let opaque = 0;
    for (let i = 0; i + 3 < pixels.length; i += 4) {
        if (pixels[i + 3] < alphaThreshold)
            continue;
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        opaque += 1;
        const key = (Math.floor(r / step) << (bits * 2)) |
            (Math.floor(g / step) << bits) |
            Math.floor(b / step);
        const bucket = buckets.get(key);
        if (bucket) {
            bucket.count += 1;
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
        }
        else {
            buckets.set(key, { count: 1, r, g, b });
        }
    }
    if (!opaque)
        return null;
    let top = { count: 0, r: 0, g: 0, b: 0 };
    for (const bucket of buckets.values()) {
        if (bucket.count > top.count)
            top = bucket;
    }
    // Trimming runs over the quantized buckets rather than every pixel, so the
    // extremes are real regions of the mark and not a handful of stray samples.
    const ranked = [...buckets.values()]
        .map((bucket) => ({
        color: { r: bucket.r / bucket.count, g: bucket.g / bucket.count, b: bucket.b / bucket.count },
        count: bucket.count,
    }))
        .map((entry) => ({ ...entry, luminance: luminanceOf(entry.color) }))
        .sort((a, b) => a.luminance - b.luminance);
    const cutoff = opaque * trim;
    const atCumulative = (fromEnd) => {
        const order = fromEnd ? [...ranked].reverse() : ranked;
        let seen = 0;
        for (const entry of order) {
            seen += entry.count;
            if (seen > cutoff)
                return entry;
        }
        return order[order.length - 1];
    };
    const darkest = atCumulative(false);
    const lightest = atCumulative(true);
    return {
        dominant: toHex({ r: top.r / top.count, g: top.g / top.count, b: top.b / top.count }),
        darkest: toHex(darkest.color),
        lightest: toHex(lightest.color),
    };
}
/** Worst contrast any part of a mark reaches on a ramp, and where. */
function worstSampleForMark(stops, ink, grayscale) {
    const fallback = {
        ratio: Infinity,
        sample: stops[0] ?? null,
    };
    const parsed = stops.map((stop) => parseColor(stop));
    // An unreadable stop means the field is unknown, not that it is safe. It
    // carries no polarity either, which `sample: null` says to the caller.
    if (parsed.some((rgb) => !rgb))
        return { ratio: 1, sample: null };
    const span = [ink?.darkest, ink?.dominant, ink?.lightest]
        .map((color) => parseColor(color ?? ""))
        .filter((rgb) => rgb !== null)
        .map((rgb) => effectiveLuminance(rgb, grayscale));
    if (!stops.length || !span.length)
        return fallback;
    const low = Math.min(...span);
    const high = Math.max(...span);
    let worst = fallback;
    for (const rgb of gradientSamples(parsed)) {
        const field = effectiveLuminance(rgb, grayscale);
        // Whichever end of the mark sits nearest the field is the one that fails.
        const nearest = field < low ? low : field > high ? high : field;
        const ratio = ratioBetween(field, nearest);
        if (ratio < worst.ratio)
            worst = { ratio, sample: toHex(rgb) };
    }
    return worst;
}
function scrimForLuminance(luminance) {
    return ratioBetween(luminance, 0) >= ratioBetween(luminance, 1) ? "#000000" : "#ffffff";
}
/** Luminance a background must not exceed (dark) or must reach (light). */
function clearanceBounds(inkLuminance, minRatio) {
    return {
        dark: (inkLuminance + 0.05) / minRatio - 0.05,
        light: minRatio * (inkLuminance + 0.05) - 0.05,
    };
}
function preferredDirection(bounds) {
    return bounds.dark >= 0 ? "darken" : "lighten";
}
/** Every color a gradient actually paints, sampled: stops plus their midpoints. */
function gradientSamples(stops) {
    const samples = [...stops];
    for (let i = 0; i + 1 < stops.length; i += 1) {
        const from = stops[i];
        const to = stops[i + 1];
        for (const t of MIDPOINT_SAMPLES) {
            samples.push({
                r: from.r + (to.r - from.r) * t,
                g: from.g + (to.g - from.g) * t,
                b: from.b + (to.b - from.b) * t,
            });
        }
    }
    return samples;
}
function worstRatio(stops, inkLuminance, grayscale) {
    let worst = Infinity;
    for (const sample of gradientSamples(stops)) {
        worst = Math.min(worst, ratioBetween(effectiveLuminance(sample, grayscale), inkLuminance));
    }
    return worst === Infinity ? 1 : worst;
}
function worstRatioAcross(stops, foreground, grayscale = 0) {
    const ink = parseColor(foreground);
    const parsed = stops.map((stop) => parseColor(stop));
    if (!ink || parsed.some((rgb) => !rgb))
        return 1;
    return worstRatio(parsed, effectiveLuminance(ink, grayscale), grayscale);
}
/** Luminance as seen through the CSS `grayscale()` the surface renders under. */
function effectiveLuminance(rgb, grayscale) {
    if (grayscale <= 0)
        return luminanceOf(rgb);
    const luma = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
    return luminanceOf({
        r: rgb.r + (luma - rgb.r) * grayscale,
        g: rgb.g + (luma - rgb.g) * grayscale,
        b: rgb.b + (luma - rgb.b) * grayscale,
    });
}
function ratioBetween(a, b) {
    const hi = Math.max(a, b);
    const lo = Math.min(a, b);
    return (hi + 0.05) / (lo + 0.05);
}
function luminanceOf({ r, g, b }) {
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}
function linearize(channel) {
    const c = clamp(channel, 0, 255) / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function parseColor(color) {
    if (typeof color !== "string")
        return null;
    const value = color.trim();
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
    if (hex) {
        const digits = hex[1];
        const full = digits.length === 3
            ? digits
                .split("")
                .map((d) => d + d)
                .join("")
            : digits;
        return {
            r: parseInt(full.slice(0, 2), 16),
            g: parseInt(full.slice(2, 4), 16),
            b: parseInt(full.slice(4, 6), 16),
        };
    }
    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
    if (rgb) {
        const channels = [rgb[1], rgb[2], rgb[3]].map(Number);
        if (channels.some((c) => !Number.isFinite(c)))
            return null;
        return { r: channels[0], g: channels[1], b: channels[2] };
    }
    return null;
}
function quantize({ r, g, b }) {
    return {
        r: Math.round(clamp(r, 0, 255)),
        g: Math.round(clamp(g, 0, 255)),
        b: Math.round(clamp(b, 0, 255)),
    };
}
function toHex({ r, g, b }) {
    const channel = (value) => Math.round(clamp(value, 0, 255)).toString(16).padStart(2, "0");
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}
function rgbToHsl({ r, g, b }) {
    const rn = clamp(r, 0, 255) / 255;
    const gn = clamp(g, 0, 255) / 255;
    const bn = clamp(b, 0, 255) / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const delta = max - min;
    const l = (max + min) / 2;
    if (delta === 0)
        return { h: 0, s: 0, l };
    const s = delta / (1 - Math.abs(2 * l - 1));
    let h;
    if (max === rn)
        h = ((gn - bn) / delta) % 6;
    else if (max === gn)
        h = (bn - rn) / delta + 2;
    else
        h = (rn - gn) / delta + 4;
    return { h: (h * 60 + 360) % 360, s, l };
}
function hslToRgb({ h, s, l }) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = ((h % 360) + 360) % 360 / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    const m = l - c / 2;
    let rgb;
    if (hp < 1)
        rgb = [c, x, 0];
    else if (hp < 2)
        rgb = [x, c, 0];
    else if (hp < 3)
        rgb = [0, c, x];
    else if (hp < 4)
        rgb = [0, x, c];
    else if (hp < 5)
        rgb = [x, 0, c];
    else
        rgb = [c, 0, x];
    return {
        r: (rgb[0] + m) * 255,
        g: (rgb[1] + m) * 255,
        b: (rgb[2] + m) * 255,
    };
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function round(value, digits) {
    const factor = 10 ** digits;
    return Math.round(value * factor) / factor;
}
//# sourceMappingURL=colorContrast.js.map